import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@cliently/ui";
import type {
  PortalAssignmentSession,
  PortalProgramStep,
} from "../../hooks/use-programs";
import { ProgramWeekCalendar } from "./ProgramWeekCalendar";
import { ProgramDayBlocks } from "./ProgramDayBlocks";
import { ExerciseStepDetailModal } from "./ExerciseStepDetailModal";
import {
  getCurrentCycleSessions,
  getSessionsForDate,
  getWeekDaysContaining,
  isSameCalendarDay,
  startOfDay,
} from "./program-calendar-utils";

type ExerciseGuidePayload = {
  name: string;
  nameFr: string | null;
  images: string[];
  youtubeUrl: string | null;
  instructions: string[];
  instructionsFr: string[];
  primaryMuscles: string[];
  category: string | null;
  level: string | null;
  equipment: string | null;
};

type ProgramCalendarViewProps = {
  programId: string;
  title: string;
  sessions: PortalAssignmentSession[];
  completedCyclesCount: number;
  clientId: string;
  onViewExerciseGuide?: (exercise: ExerciseGuidePayload) => void;
  onMarkModified?: () => void;
};

const dateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const ProgramCalendarView = ({
  programId,
  title,
  sessions,
  completedCyclesCount,
  clientId,
  onViewExerciseGuide,
  onMarkModified,
}: ProgramCalendarViewProps) => {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => startOfDay(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [openStep, setOpenStep] = useState<PortalProgramStep | null>(null);

  useEffect(() => {
    setSelectedDate(startOfDay(new Date()));
  }, [programId]);

  const currentSessions = useMemo(
    () => getCurrentCycleSessions(sessions, completedCyclesCount),
    [sessions, completedCyclesCount],
  );

  const datesWithSessions = useMemo(() => {
    const keys = new Set<string>();
    for (const session of currentSessions) {
      if (!session.scheduledAt) continue;
      keys.add(dateKey(new Date(session.scheduledAt)));
    }
    return keys;
  }, [currentSessions]);

  const daySessions = useMemo(
    () => getSessionsForDate(currentSessions, selectedDate),
    [currentSessions, selectedDate],
  );

  const hasDaySteps = useMemo(
    () => daySessions.some((session) => session.steps.length > 0),
    [daySessions],
  );

  const isTodaySelected = isSameCalendarDay(selectedDate, today);

  const weekRangeLabel = useMemo(() => {
    const days = getWeekDaysContaining(selectedDate);
    const first = days[0]!;
    const last = days[6]!;
    const fmt = (d: Date) =>
      d.toLocaleDateString(i18n.language, { day: "numeric", month: "short" });
    return `${fmt(first)} – ${fmt(last)}`;
  }, [selectedDate, i18n.language]);

  const shiftWeek = (deltaDays: number) => {
    const next = startOfDay(new Date(selectedDate));
    next.setDate(next.getDate() + deltaDays);
    setSelectedDate(next);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-foreground text-center text-lg font-bold tracking-tight">
        {title}
      </h3>

      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => shiftWeek(-7)}
          aria-label={t("programs.calendarPrevWeek", "Semaine précédente")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex flex-col items-center">
          <span className="text-foreground text-sm font-semibold capitalize tabular-nums">
            {weekRangeLabel}
          </span>
          {!isTodaySelected ? (
            <button
              type="button"
              onClick={() => setSelectedDate(startOfDay(new Date()))}
              className="text-primary text-[11px] font-medium hover:underline"
            >
              {t("programs.calendarBackToToday", "Revenir à aujourd'hui")}
            </button>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => shiftWeek(7)}
          aria-label={t("programs.calendarNextWeek", "Semaine suivante")}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <ProgramWeekCalendar
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        locale={i18n.language}
        datesWithSessions={datesWithSessions}
      />

      <div className="bg-muted/60 border-border/50 min-h-[12rem] rounded-2xl border p-4 sm:p-5">
        {!hasDaySteps ? (
          <div className="flex h-full min-h-[10rem] flex-col items-center justify-center text-center">
            <p className="text-foreground text-base font-bold">
              {isTodaySelected
                ? t(
                    "programs.noWorkoutToday",
                    "Pas de workout publié aujourd'hui",
                  )
                : t(
                    "programs.noWorkoutThisDay",
                    "Pas de séance prévue ce jour",
                  )}
            </p>
            <p className="text-muted-foreground mt-2 max-w-xs text-sm leading-relaxed">
              {t(
                "programs.noWorkoutHint",
                "Sélectionne un autre jour du calendrier pour voir les exercices prévus.",
              )}
            </p>
          </div>
        ) : (
          <ProgramDayBlocks
            sessions={daySessions}
            clientId={clientId}
            onOpenStep={setOpenStep}
            onMarkModified={onMarkModified}
          />
        )}
      </div>

      <ExerciseStepDetailModal
        open={openStep !== null}
        onOpenChange={(open) => {
          if (!open) setOpenStep(null);
        }}
        step={openStep}
        clientId={clientId}
        onViewExerciseGuide={onViewExerciseGuide}
        onSaved={onMarkModified}
      />
    </div>
  );
};
